// The open tasks of a DEAL or a CUSTOMER (spec 09 §4.14's Deal bullet, §4.15),
// for the deal page, the deal peek and the customer peek.
//
// WHY NOT `OpenTasksCard`. That card is WP-H's. When this panel was written
// its `TaskSubject` named an order or a quote only; WP-H's half of the same
// wave gave it `customer` and `opportunity` too (`hooks/activity-queries.ts`).
// This panel asks the same list route with the same filters and renders the
// task parts WP-H exports (`TaskWhat`, `TaskDue`, `TaskAssignee`,
// `canCompleteTask`), so folding it into the card is a follow-up that changes
// the test ids and nothing a reader sees. The quote and order peeks already
// use `OpenTasksCard` itself.
//
// THE SAME GATE AS THE CARD. A reader without `sales.activity.write` mounts no
// query and no control (the task list would narrow them to their own tasks,
// and a panel saying "no open tasks" of a deal that has some is a false
// answer). Complete shows to an admin on every open row and to anyone else on
// the rows the server returned as theirs under `scope=mine`.
//
// A DEAL THE READER CANNOT CHANGE GETS NO CARD. §5's task create admits an
// eligible assignee only on a deal they can read for tasks, which is a
// sales-only principal's own or assigned-customer deal: the same set the
// deal's `mutable` names (narrowing 16). Offered there, Add task meets 404
// OPPORTUNITY_NOT_FOUND, and the card's own read answers an empty page that
// would say "no open tasks" of a deal that may have some. So the card follows
// `mutable` exactly as Schedule does beside it.
//
// ADD TASK FROM A DEAL NAMES THE DEAL. The prefill carries `opportunityId`,
// which the create form sends as `opportunity_id` (`TaskPrefill`, WP-H).

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../../components/button";
import { Card } from "../../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../../components/data-table";
import { CardSkeleton, ErrorState, OfflineState } from "../../../../../components/states";
import { requestIdOf } from "../../../../../lib/request";
import { MutationFailureNote } from "../../mutation-failure-note";
import { useSalesPermissions } from "../../hooks/permissions";
import {
  canCompleteTask,
  useActivities,
  useCompleteActivity,
  type SalesActivitiesQuery,
  type SalesActivity,
} from "../../hooks/activity-queries";
import {
  completeFailureText,
  isTaskOverdue,
  NewTaskModal,
  TaskAssignee,
  TaskDue,
  TaskWhat,
  type TaskPrefill,
} from "../sales-tasks";

/** How many open tasks the panel asks for; a deal or a customer holds a
 * handful, and the task list is one link away for the rest. */
export const SUBJECT_TASK_LIMIT = 50;

export type TaskHolder =
  | {
      readonly kind: "opportunity";
      readonly id: string;
      readonly customerId: string;
      /** The deal's number, for the create form's fixed-subject line. */
      readonly label: string;
    }
  | { readonly kind: "customer"; readonly id: string; readonly label: string };

/** The prefill the create form receives: a deal's carries the deal. */
export type HolderPrefill = TaskPrefill;

export function holderPrefill(holder: TaskHolder): HolderPrefill {
  return holder.kind === "opportunity"
    ? { customerId: holder.customerId, label: holder.label, opportunityId: holder.id }
    : { customerId: holder.id, label: holder.label };
}

export function holderQuery(holder: TaskHolder, scope: "all" | "mine"): SalesActivitiesQuery {
  return {
    scope,
    status: "open",
    limit: SUBJECT_TASK_LIMIT,
    ...(holder.kind === "opportunity" ? { opportunity_id: holder.id } : { customer_id: holder.id }),
  };
}

export function SubjectTasksCard({
  holder,
  title,
  testId,
  mutable = true,
}: {
  readonly holder: TaskHolder;
  readonly title: string;
  readonly testId: string;
  /** The deal's own `mutable` (the header). A customer holder omits it. */
  readonly mutable?: boolean | undefined;
}) {
  const perms = useSalesPermissions();
  if (!perms.has("sales.activity.write") || !mutable) return null;
  return <SubjectTasksBody holder={holder} title={title} testId={testId} />;
}

function SubjectTasksBody({
  holder,
  title,
  testId,
}: {
  readonly holder: TaskHolder;
  readonly title: string;
  readonly testId: string;
}) {
  const { t } = useTranslation();
  const perms = useSalesPermissions();
  const tasks = useActivities(holderQuery(holder, "all"), undefined, holder.id.length > 0);
  const own = useActivities(
    holderQuery(holder, "mine"),
    undefined,
    holder.id.length > 0 && !perms.isAdmin,
  );
  const complete = useCompleteActivity();
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const rows = useMemo(() => tasks.data?.items ?? [], [tasks.data]);
  const ownIds = useMemo(() => new Set((own.data?.items ?? []).map((row) => row.id)), [own.data]);

  const columns: DataTableColumn<SalesActivity>[] = [
    {
      key: "what",
      header: t("sales.tasks.colWhat"),
      id: true,
      render: (row) => <TaskWhat row={row} />,
    },
    { key: "due_on", header: t("sales.tasks.colDue"), render: (row) => <TaskDue row={row} /> },
    {
      key: "assignee",
      header: t("sales.tasks.colAssignee"),
      render: (row) => <TaskAssignee row={row} />,
    },
    {
      key: "actions",
      header: t("sales.tasks.colActions"),
      actions: true,
      render: (row) =>
        canCompleteTask(perms, row, ownIds.has(row.id)) ? (
          <Button
            size="sm"
            data-testid={`${testId}-complete`}
            busy={complete.isPending && completingId === row.id}
            busyLabel={t("sales.tasks.completing")}
            disabled={complete.isPending}
            onClick={() => {
              setCompletingId(row.id);
              complete.mutate(row.id);
            }}
          >
            {t("sales.tasks.complete")}
          </Button>
        ) : null,
    },
  ];

  return (
    <Card
      className="sales-360-card"
      data-testid={testId}
      title={title}
      action={
        <Button
          size="sm"
          data-testid={`${testId}-add`}
          onClick={() => {
            complete.reset();
            setAdding(true);
          }}
        >
          {t("sales.tasks.add")}
        </Button>
      }
    >
      {tasks.data === undefined ? (
        tasks.error !== null ? (
          <div data-testid={`${testId}-error`}>
            <ErrorState
              title={t("sales.tasks.cardError")}
              retryLabel={t("sales.common.retry")}
              requestIdLabel={t("sales.common.requestId")}
              requestId={requestIdOf(tasks.error)}
              onRetry={() => {
                void tasks.refetch();
              }}
            />
          </div>
        ) : tasks.fetchStatus === "paused" ? (
          // Held until the network returns (design-system §3.9): said, never
          // a skeleton for as long as the cut lasts.
          <div data-testid={`${testId}-offline`}>
            <OfflineState title={t("sales.offline.title")} body={t("sales.offline.body")} />
          </div>
        ) : (
          <div data-testid={`${testId}-loading`}>
            <CardSkeleton lines={2} label={t("sales.common.loading")} />
          </div>
        )
      ) : (
        <DataTable
          data-testid={`${testId}-table`}
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          caption={title}
          rowTestId={`${testId}-row`}
          rowProps={(row) => ({
            "data-task-id": row.id,
            "data-origin": row.origin,
            "data-overdue": isTaskOverdue(row) ? "true" : "false",
          })}
          empty={
            <p className="sales-hint" data-testid={`${testId}-empty`}>
              {t("sales.peek.openTasksNone")}
            </p>
          }
        />
      )}
      {complete.isError ? (
        <MutationFailureNote
          data-testid={`${testId}-complete-failed`}
          text={completeFailureText(t, complete.error)}
          error={complete.error}
        />
      ) : null}
      {adding ? (
        <NewTaskModal
          prefill={holderPrefill(holder)}
          onClose={() => {
            setAdding(false);
          }}
        />
      ) : null}
    </Card>
  );
}
